export type SearchFieldOption =
  | 'Order ID'
  | 'User ID'
  | 'Contact Phone Number'
  | 'Address'
  | 'Subscription ID'
  | 'Report ID'
  | 'Follow-up ID'

export type ServiceName = 'Essential Care' | 'Enhanced Care' | 'Premium Care'

export interface OrderTag {
  label: string
  tone: 'green' | 'yellow' | 'red' | 'gray'
}

export interface OrderServiceLine {
  title: string
  tags: OrderTag[]
  scheduleText: string
  address: string
  isFollowUp?: boolean
}

export interface OrderRecord {
  orderId: string
  homeId: string
  serviceStatusLabel: string
  placedOnLabel: string
  lines: OrderServiceLine[]
  technician: { name: string; badgeId: string } | null
  payment: { amount: string; adjustment?: string; method: string }
  note?: string
  /** true = show Schedule + disabled Reschedule style; false = Reschedule enabled when not completed */
  useScheduleLabel: boolean
  scheduleDisabled: boolean
  rescheduleDisabled: boolean
  refundDisabled: boolean
  messageDisabled: boolean
  notesDisabled: boolean
  preferredTimes?: string[]
}

export const SEARCH_FIELD_OPTIONS: SearchFieldOption[] = [
  'Order ID',
  'User ID',
  'Contact Phone Number',
  'Address',
  'Subscription ID',
  'Report ID',
  'Follow-up ID',
]

export const CARE_SERVICE_STATUS_FU_PENDING_SCHEDULE = 'Follow-up-Pending Schedule'
export const CARE_SERVICE_STATUS_FU_PENDING_RESCHEDULE = 'Follow-up-Pending Reschedule'
export const CARE_SERVICE_STATUS_PENDING_SCHEDULE = 'Pending Schedule'
export const CARE_SERVICE_STATUS_PENDING_RESCHEDULE = 'Pending Reschedule'
export const CARE_SERVICE_STATUS_ASSIGNED = 'Assigned'
export const CARE_SERVICE_STATUS_COMPLETED = 'Completed'
export const CARE_SERVICE_STATUS_FU_ASSIGNED = 'Follow-up-Assigned'
export const CARE_SERVICE_STATUS_FU_COMPLETED = 'Follow-up-Completed'

export const SERVICE_STATUS_OPTIONS = [
  'All',
  'Pending Schedule',
  'Pending Reschedule',
  'Assigned',
  'Completed',
  'FU-Pending Schedule',
  'FU-Pending Reschedule',
  'FU-Assigned',
  'FU-Completed',
] as const

export type ServiceStatusFilter = (typeof SERVICE_STATUS_OPTIONS)[number]

export const TECHNICIAN_OPTIONS = ['All', 'John Larry', 'Jiading Wu', 'Li Wang'] as const

export const SERVICE_TIME_TEMPLATES: Record<ServiceName, string[]> = {
  'Essential Care': ['08:00 AM - 10:00 AM', '10:30 AM - 12:30 PM', '02:00 PM - 04:00 PM'],
  'Enhanced Care': ['08:00 AM - 10:00 AM', '10:00 AM - 12:00 PM', '01:00 PM - 03:00 PM', '03:30 PM - 05:30 PM'],
  'Premium Care': ['09:00 AM - 12:00 PM', '01:00 PM - 05:00 PM'],
}

export function addDaysInclusive(start: Date, days: number): Date {
  const d = new Date(start.getFullYear(), start.getMonth(), start.getDate())
  d.setDate(d.getDate() + (days - 1))
  return d
}

export function formatDeadline(d: Date): string {
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/** 侧栏角标：超过 99 显示 99+ */
export function formatCareSidebarBadge(n: number): string {
  if (n > 99) return '99+'
  return String(n)
}

function normStatusLabel(s: string) {
  return s.trim().toLowerCase()
}

/** 后端 service_status 枚举（snake_case）与展示文案统一归一 */
function normStatusKey(s: string) {
  return s.trim().toLowerCase().replace(/[\s-]+/g, '_')
}

/** 侧栏四个红点子菜单对应的归类（先 FU 细分，再主单 Pending） */
export type CareSidebarSubBucket = 'pend_sched' | 'pend_resched' | 'fu_pend_sched' | 'fu_pend_resched'

/** 将接口或旧文案中的 FU Pending * 统一为 Follow-up-Pending * 展示 */
export function careServiceStatusDisplayLabel(status: string | undefined): string {
  const raw = (status ?? '').trim()
  if (!raw || raw === '—') return raw || '—'
  const fromCode = careServiceStatusCodeToDisplayLabel(raw)
  if (fromCode) return fromCode
  const bucket = classifyCareSidebarSubBucket(raw)
  if (bucket === 'fu_pend_sched') return CARE_SERVICE_STATUS_FU_PENDING_SCHEDULE
  if (bucket === 'fu_pend_resched') return CARE_SERVICE_STATUS_FU_PENDING_RESCHEDULE
  return raw
}

/** 后端 service_status 枚举 → 八种展示文案之一 */
export function careServiceStatusCodeToDisplayLabel(code: string | undefined): string | null {
  const k = code?.trim().toLowerCase().replace(/[\s-]+/g, '_') ?? ''
  if (!k) return null
  if (k === 'pending_schedule') return CARE_SERVICE_STATUS_PENDING_SCHEDULE
  if (k === 'pending_reschedule') return CARE_SERVICE_STATUS_PENDING_RESCHEDULE
  if (k === 'assigned') return CARE_SERVICE_STATUS_ASSIGNED
  if (k === 'completed') return CARE_SERVICE_STATUS_COMPLETED
  if (k === 'fu_pending_schedule' || k === 'follow_up_pending_schedule') {
    return CARE_SERVICE_STATUS_FU_PENDING_SCHEDULE
  }
  if (k === 'fu_pending_reschedule' || k === 'follow_up_pending_reschedule') {
    return CARE_SERVICE_STATUS_FU_PENDING_RESCHEDULE
  }
  if (k === 'fu_assigned' || k === 'follow_up_assigned') return CARE_SERVICE_STATUS_FU_ASSIGNED
  if (k === 'fu_completed' || k === 'follow_up_completed') return CARE_SERVICE_STATUS_FU_COMPLETED
  return null
}

/** 主单已指派或已完成（可进入 Follow-up 阶段） */
export function mainServiceStatusIsAssignedOrBeyond(
  status?: string,
  code?: string,
): boolean {
  const blob = `${code ?? ''} ${status ?? ''}`.trim().toLowerCase().replace(/[\s-]+/g, '_')
  if (!blob) return false
  if (blob.includes('pending')) return false
  return blob.includes('assigned') || blob.includes('completed') || blob.includes('complete')
}

/**
 * POST `care-order/list` 的 `service_status`：接口多为 snake_case（如 `fu_pending_schedule`），
 * 卡片展示为 Follow-up-Pending Schedule / Follow-up-Pending Reschedule —— 与
 * `fu_pending_schedule` / `fu_pending_reschedule` 同一状态，仅文案不同。
 */
export function careOrderListServiceStatusApiParam(input: string | undefined): string {
  const raw = (input ?? '').trim()
  if (!raw || raw === 'all') return raw
  const bucket = classifyCareSidebarSubBucket(raw)
  if (bucket === 'fu_pend_sched') return 'fu_pending_schedule'
  if (bucket === 'fu_pend_resched') return 'fu_pending_reschedule'
  if (bucket === 'pend_sched') return 'pending_schedule'
  if (bucket === 'pend_resched') return 'pending_reschedule'
  const k = normStatusKey(raw)
  if (bucketFromServiceStatusCode(k)) return k
  return k || raw
}

function bucketFromServiceStatusCode(code: string | undefined): CareSidebarSubBucket | null {
  const k = normStatusKey(code ?? '')
  if (!k) return null
  if (k === 'fu_pending_reschedule' || k === 'follow_up_pending_reschedule') return 'fu_pend_resched'
  if (k === 'fu_pending_schedule' || k === 'follow_up_pending_schedule') return 'fu_pend_sched'
  if (k === 'pending_reschedule') return 'pend_resched'
  if (k === 'pending_schedule') return 'pend_sched'
  return null
}

export function classifyCareSidebarSubBucket(serviceStatusLabel: string): CareSidebarSubBucket | null {
  const raw = serviceStatusLabel.trim()
  if (!raw || raw === '—') return null
  const fromCode = bucketFromServiceStatusCode(raw)
  if (fromCode) return fromCode
  const n = normStatusLabel(raw)
  const k = normStatusKey(raw)

  // ⑥ Follow-up-Pending Reschedule / FU-Pending Reschedule
  if (
    k === 'fu_pending_reschedule' ||
    k === 'follow_up_pending_reschedule' ||
    n === normStatusLabel('FU-Pending Reschedule') ||
    n === normStatusLabel('FU Pending Reschedule') ||
    n === normStatusLabel('Follow-up-Pending Reschedule') ||
    (/\bfollow\s*[-_]?\s*up\b/.test(n) && n.includes('reschedule')) ||
    (n.includes('follow-up') && n.includes('reschedule')) ||
    (k.includes('follow_up') && k.includes('reschedule')) ||
    /^fu[\s_-]+pending[\s_-]+reschedule\b/.test(n) ||
    /^fu[\s_-]+pending[\s_-]+reschedule\b/.test(k)
  ) {
    return 'fu_pend_resched'
  }

  // ⑤ Follow-up-Pending Schedule / FU-Pending Schedule
  if (
    k === 'fu_pending_schedule' ||
    k === 'follow_up_pending_schedule' ||
    n === normStatusLabel('FU-Pending Schedule') ||
    n === normStatusLabel('FU Pending Schedule') ||
    n === normStatusLabel('Follow-up-Pending Schedule') ||
    (/\bfollow\s*[-_]?\s*up\b/.test(n) &&
      n.includes('pending') &&
      n.includes('schedule') &&
      !n.includes('reschedule')) ||
    (n.includes('follow-up') && n.includes('pending') && n.includes('schedule') && !n.includes('reschedule')) ||
    (k.includes('follow_up') && k.includes('pending') && k.includes('schedule') && !k.includes('reschedule')) ||
    (n.startsWith('fu-') && n.includes('pending') && n.includes('schedule') && !n.includes('reschedule')) ||
    (k.startsWith('fu_') && k.includes('pending') && k.includes('schedule') && !k.includes('reschedule'))
  ) {
    return 'fu_pend_sched'
  }

  // ② Pending Reschedule（主单）
  if (k === 'pending_reschedule' || n === normStatusLabel('Pending Reschedule')) return 'pend_resched'

  // ① Pending Schedule（主单）
  if (k === 'pending_schedule' || n === normStatusLabel('Pending Schedule')) return 'pend_sched'

  return null
}

/** 订单卡片归入侧栏四桶：优先 `service_status` 枚举，其次展示文案，再跟进行推断 */
export function classifyCareOrderCardBucket(order: {
  status?: string
  serviceStatusCode?: string
  services?: { serviceName?: string; scheduledAt?: string | null; canSchedule?: boolean; canReschedule?: boolean }[]
}): CareSidebarSubBucket | null {
  const fromCode = bucketFromServiceStatusCode(order.serviceStatusCode)
  if (fromCode === 'fu_pend_sched' || fromCode === 'fu_pend_resched') return fromCode
  const fromLabel = classifyCareSidebarSubBucket(order.status ?? '')
  if (fromLabel === 'fu_pend_sched' || fromLabel === 'fu_pend_resched') return fromLabel

  const services = order.services ?? []
  const fuLine = services.find((l) => (l.serviceName ?? '').trim().toLowerCase() === 'follow-up care')
  const mainLine = services.find((l) => (l.serviceName ?? '').trim().toLowerCase() !== 'follow-up care')
  if (!fuLine || !mainLine) return fromCode ?? fromLabel

  const mainBlob = `${order.serviceStatusCode ?? ''} ${order.status ?? ''}`.toLowerCase()
  const mainReady =
    mainBlob.includes('assigned') ||
    mainBlob.includes('completed') ||
    mainBlob.includes('complete') ||
    (!mainBlob.includes('pending') && lineHasScheduledTime(mainLine.scheduledAt))

  if (!mainReady) return fromCode ?? fromLabel

  const fuSched = (fuLine.scheduledAt ?? '').trim().toLowerCase()
  if (/pending\s*reschedule/.test(fuSched)) return 'fu_pend_resched'
  if (/pending\s*schedule/.test(fuSched)) return 'fu_pend_sched'
  // 跟进行已有具体排期（非 Pending * 占位）→ FU-Assigned，不归入 FU Pending 桶
  if (lineHasScheduledTime(fuLine.scheduledAt)) return fromCode ?? fromLabel
  if (fuLine.canSchedule && !fuLine.canReschedule) return 'fu_pend_sched'
  if (fuLine.canReschedule && !fuLine.canSchedule) return 'fu_pend_resched'

  return fromCode ?? fromLabel
}

function lineHasScheduledTime(raw: string | null | undefined): boolean {
  const s = (raw ?? '').trim()
  if (!s || s === '—') return false
  return !/^pending\s+(schedule|reschedule)/i.test(s)
}

/** 侧栏红点四项 + total（total 为列表条数；全量订单数以接口 total 为准由页面单独维护） */
export interface CareSidebarCounts {
  pendingSchedule: number
  pendingReschedule: number
  fuPendingSchedule: number
  fuPendingReschedule: number
}

/** 四子目录 POST care-order/list 的 service_status 参数 */
export const CARE_SIDEBAR_SUB_BUCKET_API_STATUS: Record<CareSidebarSubBucket, string> = {
  pend_sched: 'pending_schedule',
  pend_resched: 'pending_reschedule',
  fu_pend_sched: 'fu_pending_schedule',
  fu_pend_resched: 'fu_pending_reschedule',
}

export function careSidebarCountFieldForSubBucket(
  bucket: CareSidebarSubBucket,
): keyof CareSidebarCounts {
  if (bucket === 'pend_sched') return 'pendingSchedule'
  if (bucket === 'pend_resched') return 'pendingReschedule'
  if (bucket === 'fu_pend_sched') return 'fuPendingSchedule'
  return 'fuPendingReschedule'
}

/** 与各 Care 子菜单对应的订单数量（按订单状态归入 ①②⑤⑥） */
export function countOrdersForCareSidebar(list: OrderRecord[]) {
  let pendingSchedule = 0
  let pendingReschedule = 0
  let fuPendingSchedule = 0
  let fuPendingReschedule = 0
  for (const o of list) {
    const b = classifyCareSidebarSubBucket(o.serviceStatusLabel)
    if (b === 'pend_sched') pendingSchedule++
    else if (b === 'pend_resched') pendingReschedule++
    else if (b === 'fu_pend_sched') fuPendingSchedule++
    else if (b === 'fu_pend_resched') fuPendingReschedule++
  }
  return {
    total: list.length,
    pendingSchedule,
    pendingReschedule,
    fuPendingSchedule,
    fuPendingReschedule,
  }
}

/** Seed list — a few orders for the list UI */
export const MOCK_ORDERS: OrderRecord[] = [
  {
    orderId: 'HM-10492',
    homeId: 'H-88301',
    serviceStatusLabel: 'Pending Schedule',
    placedOnLabel: 'Dec 1, 2025, 08:01 AM',
    lines: [
      {
        title: 'Enhanced Care',
        tags: [
          { label: 'P-17', tone: 'green' },
        ],
        scheduleText: 'Pending Schedule',
        address: '13 Evanshen Cres, Kanata, ON K2K 2Z6',
      },
    ],
    technician: null,
    payment: { amount: '$106.22', method: 'Credit Card' },
    note: 'Customer requested morning slot if possible.',
    useScheduleLabel: true,
    scheduleDisabled: false,
    rescheduleDisabled: true,
    refundDisabled: false,
    messageDisabled: false,
    notesDisabled: false,
    preferredTimes: ['Dec 05, 2025, 08:00 AM - 10:00 AM', 'Dec 06, 2025, 08:00 AM - 10:00 AM', 'Dec 07, 2025, 10:00 AM - 12:00 PM'],
  },
  {
    orderId: 'MOCK-ORD-002',
    homeId: 'MOCK-HOME-002',
    serviceStatusLabel: 'Assigned',
    placedOnLabel: 'May 21, 2026, 08:49 AM',
    lines: [
      {
        title: 'Enhanced Care',
        tags: [],
        scheduleText: 'May 25, 2026, 01:30 PM - 04:30 PM',
        address: '606 Harbour View Rd Unit 1608, Vancouver, BC V6B 2X1',
      },
    ],
    technician: { name: 'Sam Taylor', badgeId: 'T0009003' },
    payment: { amount: '$293.80', method: 'Credit Card' },
    note: 'Concierge requires technician ID at arrival.',
    useScheduleLabel: false,
    scheduleDisabled: true,
    rescheduleDisabled: false,
    refundDisabled: false,
    messageDisabled: false,
    notesDisabled: false,
  },
  {
    orderId: 'HM-10470',
    homeId: 'H-88102',
    serviceStatusLabel: 'Follow-up-Assigned',
    placedOnLabel: 'Nov 20, 2025, 09:40 AM',
    lines: [
      {
        title: 'Enhanced Care',
        tags: [{ label: 'N-10', tone: 'green' }],
        scheduleText: 'Dec 02, 2025, 08:00 AM - 10:00 AM',
        address: '13 Evanshen Cres, Kanata, ON K2K 2Z6',
      },
      {
        title: 'Follow-up Care',
        tags: [{ label: 'FU-1', tone: 'gray' }],
        scheduleText: 'Dec 18, 2025, 01:00 PM - 03:00 PM',
        address: '13 Evanshen Cres, Kanata, ON K2K 2Z6',
        isFollowUp: true,
      },
    ],
    technician: { name: 'John Larry', badgeId: 'ON-355935' },
    payment: { amount: '$189.00', method: 'Credit Card' },
    useScheduleLabel: false,
    scheduleDisabled: true,
    rescheduleDisabled: false,
    refundDisabled: false,
    messageDisabled: false,
    notesDisabled: false,
  },
  {
    orderId: 'HM-10455',
    homeId: 'H-88001',
    serviceStatusLabel: 'Completed',
    placedOnLabel: 'Oct 12, 2025, 11:22 AM',
    lines: [
      {
        title: 'Essential Care',
        tags: [{ label: 'E-01', tone: 'green' }],
        scheduleText: 'Oct 20, 2025, 10:00 AM - 12:00 PM',
        address: '2000 Meadowbrook Rd, Ottawa, ON K1V 9Z6',
      },
    ],
    technician: { name: 'Jiading Wu', badgeId: 'ON-441201' },
    payment: { amount: '$79.50', method: 'Credit Card' },
    useScheduleLabel: false,
    scheduleDisabled: true,
    rescheduleDisabled: true,
    refundDisabled: false,
    messageDisabled: false,
    notesDisabled: false,
  },
]
